import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AuthError, passwordProblem, resetPassword } from '../api/auth'
import { useSetSession } from '../useSession'
import AuthLayout, { FormError, PasswordFields, SubmitButton } from './AuthLayout'

/**
 * Sets a new password from a reset link.
 *
 * Deliberately the same shape as accepting an invitation, because it is the
 * same act from the person's side: prove you own the mailbox, choose a
 * password, get signed in. Diverging would mean two screens to keep correct.
 */
export default function ResetPasswordView() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<AuthError | null>(null)
  const [busy, setBusy] = useState(false)
  const [attempted, setAttempted] = useState(false)

  const navigate = useNavigate()
  const setSession = useSetSession()

  // No token means nothing here can succeed, so the form is never shown —
  // filling it in and then failing wastes the person's time. Email clients
  // genuinely do cut long links short.
  if (!token) {
    return (
      <AuthLayout
        title="This link is incomplete"
        subtitle="The reset link is missing its code. It may have been cut short by an email client."
      >
        <FormError
          title="Nothing to reset"
          detail="Ask for a fresh link and open it directly from the email rather than copying it."
        />
        <p className="mt-5 text-center text-[13px] text-ink-3">
          <Link to="/forgot-password" className="font-semibold text-accent">
            Send me a new link
          </Link>
        </p>
      </AuthLayout>
    )
  }

  const problem = passwordProblem(password, confirm)

  async function submit(event: FormEvent) {
    event.preventDefault()

    // Never a silent return (D-186).
    setAttempted(true)
    if (problem) return

    setBusy(true)
    setError(null)

    try {
      setSession(await resetPassword(token, password))

      // Resetting signs you in server-side; sending you to the login screen
      // would ask for the password chosen thirty seconds ago.
      navigate('/', { replace: true })
    } catch (e) {
      setError(
        e instanceof AuthError
          ? e
          : new AuthError('Could not reach the studio system. Check your connection.', 0),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="Pick a password and you will be signed in."
    >
      {error && <FormError title={error.message} detail={error.detail} />}

      <form onSubmit={submit} noValidate>
        <PasswordFields
          password={password}
          confirm={confirm}
          onPassword={setPassword}
          onConfirm={setConfirm}
          problem={problem}
          showProblem={attempted}
        />

        <SubmitButton busy={busy}>Set password and continue</SubmitButton>
      </form>

      {/*
        Signing in elsewhere ends here too — the reset invalidates every other
        session, which is the point of doing it after a suspected compromise.
      */}
      <p className="mt-5 text-[12.5px] leading-relaxed text-ink-3">
        Setting a new password signs you out everywhere else.
      </p>
    </AuthLayout>
  )
}
